import { db } from '../firebase.js'; // Assuming db is exported from firebase.js
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  serverTimestamp,
  where,
  writeBatch,
  limit,
  arrayUnion, // Needed for managing teacherIds
  arrayRemove, // Needed for managing teacherIds
  setDoc,
  getDoc,
  Timestamp
} from 'firebase/firestore';
import { removeCreatedClassroomFromTeacher, addCreatedClassroomToTeacher } from './teacherService.js'; // Import teacher service

export const ALL_STUDENTS_CLASSROOM_ID = 'all-students';

const buildAllStudentsVirtualClassroom = () => ({
  id: ALL_STUDENTS_CLASSROOM_ID,
  name: 'Todos os alunos',
  description: 'Visão consolidada de todos os alunos, incluindo os sem turma.',
  teacherIds: [],
  categoryIds: [],
  isAggregateView: true,
  inviteUrl: null,
  inviteToken: null,
  inviteExpiresAt: null,
  creatorId: null
});

// Reference to the 'classrooms' collection in Firestore
const classroomsCollectionRef = collection(db, 'classrooms');
// Reference to the 'students' collection (needed for delete cleanup)
const studentsCollectionRef = collection(db, 'students');

// Invite token configuration
const INVITE_TOKEN_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
const INVITE_TOKEN_LENGTH = 24;

const classroomInviteConverter = {
  toFirestore(invite) {
    const {
      id: _ignore,
      classroomId,
      token,
      creatorId,
      createdAt,
      updatedAt,
      expiresAt,
      lastRotatedBy,
      rotatedAt
    } = invite || {};

    const data = {};

    if (typeof classroomId !== 'undefined') data.classroomId = classroomId;
    if (typeof token === 'string' && token.length > 0) data.token = token;
    if (typeof creatorId !== 'undefined') data.creatorId = creatorId;
    if (createdAt) data.createdAt = createdAt;
    if (updatedAt) data.updatedAt = updatedAt;
    if (rotatedAt) data.rotatedAt = rotatedAt;
    if (typeof lastRotatedBy !== 'undefined') data.lastRotatedBy = lastRotatedBy;
    if (typeof expiresAt !== 'undefined') data.expiresAt = expiresAt;

    return data;
  },
  fromFirestore(snapshot, options) {
    const data = snapshot.data(options) || {};
    return {
      id: snapshot.id,
      classroomId: data.classroomId ?? snapshot.id,
      token: data.token ?? '',
      creatorId: data.creatorId ?? null,
      createdAt: data.createdAt ?? null,
      updatedAt: data.updatedAt ?? null,
      expiresAt: data.expiresAt ?? null,
      lastRotatedBy: data.lastRotatedBy ?? null,
      rotatedAt: data.rotatedAt ?? null
    };
  }
};

const classroomInvitesCollectionRef = collection(db, 'classroomInvites').withConverter(classroomInviteConverter);

const inviteDocRef = (classroomId) => doc(classroomInvitesCollectionRef, classroomId);

const trimTrailingSlash = (value) => value.replace(/\/+$/, '');

let cachedInviteBaseUrl;

const resolveInviteBaseUrl = () => {
  if (cachedInviteBaseUrl !== undefined) {
    return cachedInviteBaseUrl;
  }

  const env = (typeof process !== 'undefined' && process.env) ? process.env : {};
  const candidate = [
    env.NEXT_PUBLIC_SITE_URL,
    env.REACT_APP_SITE_URL,
    env.PUBLIC_URL
  ].find((url) => typeof url === 'string' && url.trim().length > 0);

  if (candidate) {
    cachedInviteBaseUrl = trimTrailingSlash(candidate.trim());
    return cachedInviteBaseUrl;
  }

  if (typeof window !== 'undefined' && window?.location?.origin) {
    cachedInviteBaseUrl = trimTrailingSlash(window.location.origin);
    return cachedInviteBaseUrl;
  }

  cachedInviteBaseUrl = null;
  return cachedInviteBaseUrl;
};

const buildInviteUrl = (token) => {
  if (!token) return null;

  const baseUrl = resolveInviteBaseUrl();
  if (!baseUrl) return null;

  return `${baseUrl}/classrooms/${token}/join`;
};

const withInviteUrl = (invite) => {
  if (!invite) return null;

  return {
    ...invite,
    inviteUrl: buildInviteUrl(invite.token ?? null)
  };
};

const toTimestamp = (value) => {
  if (!value) return null;
  if (value instanceof Timestamp) return value;
  if (value instanceof Date) return Timestamp.fromDate(value);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : Timestamp.fromDate(parsed);
};

const getRandomValues = (size) => {
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const buffer = new Uint32Array(size);
    crypto.getRandomValues(buffer);
    return Array.from(buffer);
  }

  // Fallback for environments without Web Crypto (e.g., unit tests)
  return Array.from({ length: size }, () => Math.floor(Math.random() * 0xffffffff));
};

const generateInviteToken = () => {
  const randomValues = getRandomValues(INVITE_TOKEN_LENGTH);
  const alphabetLength = INVITE_TOKEN_ALPHABET.length;
  let token = '';

  for (let i = 0; i < INVITE_TOKEN_LENGTH; i += 1) {
    token += INVITE_TOKEN_ALPHABET.charAt(randomValues[i] % alphabetLength);
  }

  return token;
};

const writeInviteDocument = async (classroomId, payload, { merge = false } = {}) => {
  const ref = inviteDocRef(classroomId);
  await setDoc(ref, payload, { merge });
  const snapshot = await getDoc(ref);
  return snapshot.exists() ? withInviteUrl(snapshot.data()) : null;
};

const getInviteByToken = async (token) => {
  if (!token) {
    return null;
  }

  const sanitizedToken = (typeof token === 'string' ? token : String(token)).trim();
  if (!sanitizedToken) {
    return null;
  }

  const inviteQuery = query(
    classroomInvitesCollectionRef,
    where('token', '==', sanitizedToken),
    limit(1)
  );

  const snapshot = await getDocs(inviteQuery);

  if (snapshot.empty) {
    return null;
  }

  return snapshot.docs[0]?.data() ?? null;
};

export const getClassroomInvite = async (classroomId) => {
  if (!classroomId) {
    throw new Error('Classroom ID is required to load invite metadata.');
  }

  const snapshot = await getDoc(inviteDocRef(classroomId));
  return snapshot.exists() ? withInviteUrl(snapshot.data()) : null;
};

export const ensureClassroomInvite = async (classroomId, teacherId = null, options = {}) => {
  if (!classroomId) {
    throw new Error('Classroom ID is required to ensure invite token.');
  }

  const existing = await getClassroomInvite(classroomId);
  if (existing && existing.token) {
    if (Object.prototype.hasOwnProperty.call(options, 'expiresAt')) {
      const expiresAt = toTimestamp(options.expiresAt);
      const payload = {
        updatedAt: serverTimestamp()
      };
      if (expiresAt) {
        payload.expiresAt = expiresAt;
      } else if (options.expiresAt === null) {
        payload.expiresAt = null;
      }
      await writeInviteDocument(classroomId, payload, { merge: true });
    }
    return withInviteUrl(existing);
  }

  const expiresAt = toTimestamp(options.expiresAt);
  const now = serverTimestamp();

  const payload = {
    classroomId,
    token: generateInviteToken(),
    creatorId: teacherId ?? null,
    createdAt: now,
    updatedAt: now
  };

  if (expiresAt) {
    payload.expiresAt = expiresAt;
  } else if (options.expiresAt === null) {
    payload.expiresAt = null;
  }

  return withInviteUrl(await writeInviteDocument(classroomId, payload));
};

export const rotateClassroomInviteToken = async (classroomId, actorId, options = {}) => {
  if (!classroomId) {
    throw new Error('Classroom ID is required to rotate invite token.');
  }

  const expiresAt = Object.prototype.hasOwnProperty.call(options, 'expiresAt')
    ? toTimestamp(options.expiresAt)
    : undefined;

  const now = serverTimestamp();

  const payload = {
    classroomId,
    token: generateInviteToken(),
    updatedAt: now,
    rotatedAt: now
  };

  if (typeof actorId !== 'undefined') {
    payload.lastRotatedBy = actorId ?? null;
  }

  if (expiresAt !== undefined) {
    if (expiresAt) {
      payload.expiresAt = expiresAt;
    } else {
      payload.expiresAt = null;
    }
  }

  const existing = await getClassroomInvite(classroomId);
  if (!existing) {
    payload.createdAt = now;
    payload.creatorId = (typeof actorId !== 'undefined' && actorId !== null) ? actorId : null;
  }

  const updated = await writeInviteDocument(classroomId, payload, { merge: true });
  return withInviteUrl(updated);
};

const hydrateClassroomWithInvite = async (classroom) => {
  if (!classroom || !classroom.id) {
    return classroom;
  }

  if (classroom.isAggregateView || classroom.id === ALL_STUDENTS_CLASSROOM_ID) {
    return {
      ...classroom,
      inviteToken: null,
      inviteUrl: null,
      inviteExpiresAt: null
    };
  }

  try {
    let invite = await getClassroomInvite(classroom.id);

    if ((!invite || !invite.token) && classroom.creatorId) {
      try {
        invite = await ensureClassroomInvite(classroom.id, classroom.creatorId);
      } catch (ensureError) {
        console.error(`Error ensuring classroom invite token while hydrating classroom ${classroom.id}:`, ensureError);
      }
    }

    return {
      ...classroom,
      inviteToken: invite?.token ?? null,
      inviteUrl: invite?.inviteUrl ?? null,
      inviteExpiresAt: invite?.expiresAt ?? null
    };
  } catch (error) {
    console.error(`Error fetching classroom invite metadata for classroom ${classroom.id}:`, error);
    return {
      ...classroom,
      inviteToken: null,
      inviteUrl: null,
      inviteExpiresAt: null
    };
  }
};

// Get all classrooms, optionally filtered by teacher, ordered by name
export const getAllClassrooms = async (teacherId = null) => {
  try {
    let q;

    if (teacherId) {
      // No orderBy here → avoids composite‑index requirement
      q = query(
        classroomsCollectionRef,
        where('teacherIds', 'array-contains', teacherId)
      );
    } else {
      // No filter → we can safely order on the server
      q = query(classroomsCollectionRef, orderBy('name'));
    }

    const snapshot = await getDocs(q);

    // Map documents to objects
    let classrooms = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Client‑side sort when we skipped orderBy above
    if (teacherId) {
      classrooms.sort((a, b) => a.name.localeCompare(b.name));
    }

    const hydrated = await Promise.all(classrooms.map(hydrateClassroomWithInvite));

    return hydrated;
  } catch (error) {
    console.error('Error fetching classrooms:', error);
    throw new Error('Failed to fetch classrooms from Firestore.');
  }
};

export const getClassroomById = async (classroomId, { includeInvite = true } = {}) => {
  if (!classroomId) {
    throw new Error('Classroom ID is required to fetch classroom details.');
  }

  if (classroomId === ALL_STUDENTS_CLASSROOM_ID) {
    return buildAllStudentsVirtualClassroom();
  }

  const classroomRef = doc(db, 'classrooms', classroomId);
  const snapshot = await getDoc(classroomRef);

  if (!snapshot.exists()) {
    return null;
  }

  const classroom = {
    id: snapshot.id,
    ...snapshot.data()
  };

  if (!includeInvite) {
    return classroom;
  }

  return hydrateClassroomWithInvite(classroom);
};

export const getClassroomByInviteToken = async (token) => {
  if (!token) {
    throw new Error('Invite token is required to fetch classroom by invite.');
  }

  const invite = await getInviteByToken(token);

  if (!invite) {
    return null;
  }

  const hydratedInvite = withInviteUrl(invite);

  const classroomId = hydratedInvite?.classroomId;
  if (!classroomId) {
    return { invite: hydratedInvite ?? null, classroom: null };
  }

  try {
    const classroomRef = doc(db, 'classrooms', classroomId);
    const classroomSnapshot = await getDoc(classroomRef);

    if (!classroomSnapshot.exists()) {
      return { invite: hydratedInvite ?? null, classroom: null };
    }

    const classroom = {
      id: classroomSnapshot.id,
      ...classroomSnapshot.data(),
      inviteToken: hydratedInvite?.token ?? null,
      inviteUrl: hydratedInvite?.inviteUrl ?? null,
      inviteExpiresAt: hydratedInvite?.expiresAt ?? null
    };

    return { invite: hydratedInvite ?? null, classroom };
  } catch (error) {
    console.error(`Error fetching classroom for invite token ${token}:`, error);
    return { invite: hydratedInvite ?? null, classroom: null };
  }
};

// Add a new classroom
export const addClassroom = async (classroomData, creatorId) => {
  if (!creatorId) {
    throw new Error("Creator ID is required to add a classroom.");
  }
  // Ensure categoryIds is always an array, even if empty
  // Initialize teacherIds with the creatorId
  const dataToAdd = {
    ...classroomData,
    creatorId: creatorId, // Add creator ID
    teacherIds: [creatorId], // Initialize teacherIds array with the creator
    categoryIds: classroomData.categoryIds || [],
    isDefault: classroomData.isDefault || false, // Ensure isDefault is handled
    createdAt: serverTimestamp()
  };
  console.log("Data to add:", dataToAdd);
  try {
    const docRef = await addDoc(classroomsCollectionRef, dataToAdd);
    console.log("Document reference:", docRef);

    // Add the classroom ID to the teacher's createdClassroomIds list
    await addCreatedClassroomToTeacher(creatorId, docRef.id);

    try {
      await ensureClassroomInvite(docRef.id, creatorId);
    } catch (inviteError) {
      console.error('Error ensuring classroom invite token during creation:', inviteError);
    }

    return docRef.id; // Return the new classroom's ID
  } catch (error) {
    console.error("Error adding classroom: ", error);
    throw new Error("Failed to add classroom to Firestore.");
  }
};

// Update an existing classroom
export const updateClassroom = async (classroomId, updatedData) => {
   // Ensure categoryIds and teacherIds are always arrays
   // Exclude creatorId from being updated here
   const { creatorId, ...restData } = updatedData;
   const dataToUpdate = {
    ...restData,
    categoryIds: updatedData.categoryIds || [],
    teacherIds: updatedData.teacherIds || [], // Make sure teacherIds is managed correctly
    updatedAt: serverTimestamp()
  };

  // Handle isDefault logic if needed (as before)
  if (updatedData.isDefault) {
    try {
      const defaultClassroomQuery = query(classroomsCollectionRef, where("isDefault", "==", true));
      const defaultClassroomSnapshot = await getDocs(defaultClassroomQuery);
      const batch = writeBatch(db);
      defaultClassroomSnapshot.forEach((doc) => {
        if (doc.id !== classroomId) {
          batch.update(doc.ref, { isDefault: false });
        }
      });
      await batch.commit();
    } catch (error) {
      console.error("Error updating default classroom status: ", error);
      // Continue with the update anyway
    }
  }
  try {
    const classroomDocRef = doc(db, 'classrooms', classroomId);
    await updateDoc(classroomDocRef, dataToUpdate);

    try {
      await ensureClassroomInvite(classroomId, creatorId ?? null);
    } catch (inviteError) {
      console.error('Error ensuring classroom invite token during update:', inviteError);
    }
  } catch (error) {
    console.error("Error updating classroom: ", error);
    throw new Error("Failed to update classroom in Firestore.");
  }
};

// Get default classroom (no changes needed here)
export const getDefaultClassroom = async () => {
  try {
    const q = query(classroomsCollectionRef, where("isDefault", "==", true), limit(1));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data()
      };
    }

    // If no default classroom, return the first classroom
    const allClassroomsQuery = query(classroomsCollectionRef, orderBy('name'), limit(1));
    const allClassroomsSnapshot = await getDocs(allClassroomsQuery);

    if (!allClassroomsSnapshot.empty) {
      const doc = allClassroomsSnapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data()
      };
    }

    return null; // No classrooms available
  } catch (error) {
    console.error("Error fetching default classroom: ", error);
    throw new Error("Failed to fetch default classroom from Firestore.");
  }
};

// Delete a classroom
export const deleteClassroom = async (classroomId, creatorId) => {
  const classroomDocRef = doc(db, 'classrooms', classroomId);
  const batch = writeBatch(db);

  try {
    // Find students associated with this classroom
    const studentsQuery = query(studentsCollectionRef, where("classroomId", "==", classroomId));
    const studentsSnapshot = await getDocs(studentsQuery);

    // Unlink each student
    studentsSnapshot.forEach((studentDoc) => {
      batch.update(studentDoc.ref, { classroomId: null });
    });

    // Add the classroom deletion to the batch
    batch.delete(classroomDocRef);
    batch.delete(inviteDocRef(classroomId));

    // Commit the batch
    await batch.commit();

    // Remove the classroom ID from the creator teacher's record
    if (creatorId) {
        await removeCreatedClassroomFromTeacher(creatorId, classroomId);
    } else {
        // If creatorId wasn't passed, try to fetch it (less efficient)
        // This path might occur if delete is called without creator context
        console.warn("Attempting classroom delete without creatorId - may not update teacher record.");
    }

  } catch (error) {
    console.error("Error deleting classroom and updating students: ", error);
    throw new Error("Failed to delete classroom or update associated students in Firestore.");
  }
};

// Function to assign a teacher to a classroom
export const assignTeacherToClassroom = async (classroomId, teacherId) => {
    if (!classroomId || !teacherId) return;
    try {
        const classroomRef = doc(db, 'classrooms', classroomId);
        await updateDoc(classroomRef, {
            teacherIds: arrayUnion(teacherId) // Adds teacherId if not already present
        });
    } catch (error) {
        console.error(`Error assigning teacher ${teacherId} to classroom ${classroomId}:`, error);
        throw new Error("Failed to assign teacher.");
    }
}

// Function to remove a teacher from a classroom
export const removeTeacherFromClassroom = async (classroomId, teacherId) => {
     if (!classroomId || !teacherId) return;
    try {
        const classroomRef = doc(db, 'classrooms', classroomId);
        await updateDoc(classroomRef, {
            teacherIds: arrayRemove(teacherId) // Removes all instances of teacherId
        });
    } catch (error) {
        console.error(`Error removing teacher ${teacherId} from classroom ${classroomId}:`, error);
        throw new Error("Failed to remove teacher.");
    }
}